import { Injectable, inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../api/api.service';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth.interceptor';

export type ReservationOtpChannel = 'phone' | 'email';

export interface ReservationSessionRecord {
    id: string;
    propertyId: string | null;
    paymentPlanId: string | null;
    userId: string | null;
    reservationId: string | null;
    status: string | null;
    fullName: string | null;
    email: string | null;
    phone: string | null;
    verifiedChannel: ReservationOtpChannel | null;
    verifiedTarget: string | null;
    metadata: Record<string, any>;
    lastError: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class ReservationSessionService {
    private api = inject(ApiService);

    validateContact(payload: {
        phone?: string | null;
        email?: string | null;
        sessionId?: string | null;
        propertyId?: string | null;
        paymentPlanId?: string | null;
        fullName?: string | null;
    }): Observable<{ ok: boolean; email: string | null; phone: string | null }> {
        const body: any = {
            phone: payload.phone ?? undefined,
            email: payload.email ?? undefined,
            session_id: payload.sessionId ?? undefined,
            property_id: payload.propertyId ?? undefined,
            payment_plan_id: payload.paymentPlanId ?? undefined,
            full_name: payload.fullName ?? undefined
        };
        const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
        return this.api.post<any>('/API2/fast-reservation/contact/validate', body, { context }).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
    map((data: any) => ({
      ok: data?.ok !== false && data?.OK !== false,
      email: data?.email ?? data?.EMAIL ?? null,
      phone: data?.phone ?? data?.PHONE ?? null
    }))
        );
    }

    sendOtp(payload: {
        channel: ReservationOtpChannel;
        phone?: string | null;
        email?: string | null;
        sessionId?: string | null;
        propertyId?: string | null;
        paymentPlanId?: string | null;
        fullName?: string | null;
    }): Observable<{
        channel: ReservationOtpChannel;
        target: string | null;
        target_masked: string | null;
        fast_reservation_session_id: string | null;
    }> {
        const body: any = {
            channel: payload.channel,
            phone: payload.phone ?? undefined,
            email: payload.email ?? undefined,
            session_id: payload.sessionId ?? undefined,
            property_id: payload.propertyId ?? undefined,
            payment_plan_id: payload.paymentPlanId ?? undefined,
            full_name: payload.fullName ?? undefined
        };
        const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
        return this.api.post<any>('/API2/fast-reservation/otp/send', body, { context }).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                channel: (data?.CHANNEL ?? data?.channel) === 'email' ? 'email' : 'phone',
                target: data?.TARGET ?? data?.target ?? null,
                target_masked: data?.TARGET_MASKED ?? data?.target_masked ?? null,
                fast_reservation_session_id: data?.FAST_RESERVATION_SESSION_ID ?? data?.fast_reservation_session_id ?? null
            }))
        );
    }

    verifyOtp(payload: {
        channel: ReservationOtpChannel;
        code: string;
        phone?: string | null;
        email?: string | null;
        sessionId?: string | null;
        propertyId?: string | null;
        paymentPlanId?: string | null;
        fullName?: string | null;
    }): Observable<{
        channel: ReservationOtpChannel;
        target: string | null;
        target_masked: string | null;
        verified: boolean;
        fast_reservation_session_id: string | null;
    }> {
        const body: any = {
            channel: payload.channel,
            code: payload.code,
            phone: payload.phone ?? undefined,
            email: payload.email ?? undefined,
            session_id: payload.sessionId ?? undefined,
            property_id: payload.propertyId ?? undefined,
            payment_plan_id: payload.paymentPlanId ?? undefined,
            full_name: payload.fullName ?? undefined
        };
        const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
        return this.api.post<any>('/API2/fast-reservation/otp/verify', body, { context }).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                channel: (data?.CHANNEL ?? data?.channel) === 'email' ? 'email' : 'phone',
                target: data?.TARGET ?? data?.target ?? null,
                target_masked: data?.TARGET_MASKED ?? data?.target_masked ?? null,
                verified: Boolean(data?.VERIFIED ?? data?.verified),
                fast_reservation_session_id: data?.FAST_RESERVATION_SESSION_ID ?? data?.fast_reservation_session_id ?? null
            }))
        );
    }

    /**
     * Create a reservation session for a previously-added investor (no OTP required).
     * The session is created in otp_verified status so the subsequent POST /reservations
     * uses the investor's user_id and skips identity eligibility checks.
     */
    createInvestorSession(payload: {
        investorUserId: string;
        propertyId?: string | null;
        paymentPlanId?: string | null;
        fullName?: string | null;
        email?: string | null;
        phone?: string | null;
    }): Observable<{
        fast_reservation_session_id: string | null;
        user_id: string | null;
        email: string | null;
        phone: string | null;
        verified_channel: string | null;
        verified_target: string | null;
    }> {
        const body: any = {
            user_id: payload.investorUserId,
            property_id: payload.propertyId ?? undefined,
            payment_plan_id: payload.paymentPlanId ?? undefined,
            full_name: payload.fullName ?? undefined,
            email: payload.email ?? undefined,
            phone: payload.phone ?? undefined
        };
        return this.api.post<any>('/API2/fast-reservation/session/create-for-investor', body).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                fast_reservation_session_id: data?.FAST_RESERVATION_SESSION_ID ?? data?.fast_reservation_session_id ?? null,
                user_id: data?.USER_ID ?? data?.user_id ?? null,
                email: data?.EMAIL ?? data?.email ?? null,
                phone: data?.PHONE ?? data?.phone ?? null,
                verified_channel: data?.VERIFIED_CHANNEL ?? data?.verified_channel ?? null,
                verified_target: data?.VERIFIED_TARGET ?? data?.verified_target ?? null
            }))
        );
    }

    getSession(sessionId: string): Observable<ReservationSessionRecord> {
        const id = String(sessionId || '').trim();
        return this.api.get<any>(`/API2/fast-reservation/sessions/${encodeURIComponent(id)}`).pipe(
            map((resp) => resp?.data ?? resp?.record?.data ?? resp ?? {}),
            map((data: any) => ({
                id: String(data?.ROWID ?? data?.id ?? id),
                propertyId: data?.PROPERTY_ID ?? data?.property_id ?? null,
                paymentPlanId: data?.PAYMENT_PLAN_ID ?? data?.payment_plan_id ?? null,
                userId: data?.USER_ID ?? data?.user_id ?? null,
                reservationId: data?.RESERVATION_ID ?? data?.reservation_id ?? null,
                status: data?.STATUS ?? data?.status ?? null,
                fullName: data?.FULL_NAME ?? data?.full_name ?? null,
                email: data?.EMAIL ?? data?.email ?? null,
                phone: data?.PHONE ?? data?.phone ?? null,
                verifiedChannel: data?.VERIFIED_CHANNEL === 'email' ? 'email' : data?.VERIFIED_CHANNEL === 'phone' ? 'phone' : data?.verified_channel === 'email' ? 'email' : data?.verified_channel === 'phone' ? 'phone' : null,
                verifiedTarget: data?.VERIFIED_TARGET ?? data?.verified_target ?? null,
                metadata: data?.METADATA && typeof data?.METADATA === 'object' ? data.METADATA : data?.metadata && typeof data?.metadata === 'object' ? data.metadata : {},
                lastError: data?.LAST_ERROR ?? data?.last_error ?? null,
                createdAt: data?.CREATED_AT ?? data?.created_at ?? null,
                updatedAt: data?.UPDATED_AT ?? data?.updated_at ?? null
            }))
        );
    }

    listSessions(options: { search?: string; page?: number; perPage?: number } = {}): Observable<{
        items: ReservationSessionRecord[];
        page: number;
        perPage: number;
        total: number;
    }> {
        const params = new URLSearchParams();
        if (options.search) params.set('search', String(options.search));
        if (options.page) params.set('page', String(options.page));
        if (options.perPage) params.set('per_page', String(options.perPage));
        const qs = params.toString();
        const url = `/API2/fast-reservation/sessions${qs ? `?${qs}` : ''}`;
        return this.api.get<any>(url).pipe(
            map((resp) => resp ?? {}),
            map((resp: any) => {
                const meta = resp?.meta || {};
                const data = Array.isArray(resp?.data) ? resp.data : [];
                const items: ReservationSessionRecord[] = data.map((row: any) => ({
                    id: String(row?.ROWID ?? row?.id ?? ''),
                    propertyId: row?.PROPERTY_ID ?? row?.property_id ?? null,
                    paymentPlanId: row?.PAYMENT_PLAN_ID ?? row?.payment_plan_id ?? null,
                    userId: row?.USER_ID ?? row?.user_id ?? null,
                    reservationId: row?.RESERVATION_ID ?? row?.reservation_id ?? null,
                    status: row?.STATUS ?? row?.status ?? null,
                    fullName: row?.FULL_NAME ?? row?.full_name ?? null,
                    email: row?.EMAIL ?? row?.email ?? null,
                    phone: row?.PHONE ?? row?.phone ?? null,
                    verifiedChannel: row?.VERIFIED_CHANNEL === 'email' ? 'email' : row?.VERIFIED_CHANNEL === 'phone' ? 'phone' : row?.verified_channel === 'email' ? 'email' : row?.verified_channel === 'phone' ? 'phone' : null,
                    verifiedTarget: row?.VERIFIED_TARGET ?? row?.verified_target ?? null,
                    metadata: row?.METADATA && typeof row?.METADATA === 'object' ? row.METADATA : row?.metadata && typeof row?.metadata === 'object' ? row.metadata : {},
                    lastError: row?.LAST_ERROR ?? row?.last_error ?? null,
                    createdAt: row?.CREATED_AT ?? row?.created_at ?? null,
                    updatedAt: row?.UPDATED_AT ?? row?.updated_at ?? null
                }));
                return {
                    items,
                    page: Number(meta?.page || 1),
                    perPage: Number(meta?.per_page || meta?.perPage || items.length),
                    total: Number(meta?.total || items.length)
                };
            })
        );
    }
}
